import { Modal, Result } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { usePrevious } from "../common/customHooks";
import { ActionStatus } from "../common/enums";
import ErrorResult from "../components/atoms/ErrorResult";
import LoadingOverlay from "../components/atoms/LoadingOverlay";
import SuccessResult from "../components/atoms/SuccessResult";
import DataSyndicationSourceSubscriptionList from "../components/molecules/DataSyndicationSourceSubscriptionList";
import { createDataSyndicationSourceSubscriptionRequest, getDataSyndicationSourceSubscriptionListRequest } from "../store/dataSyndicationSource/actions";
import { deleteDataSyndicationSubscriptionRequest } from "../store/dataSyndicationSubscription/actions";

interface SubscriptionListResult {
    Items: IDataSyndicationSourceSubscription[];
    sourceId: string | null;
}

interface DataSyndicationSourceSubscriptionProps {
    sourceId?: string;
    readOnly?: boolean;
    subscriptionListResult: SubscriptionListResult;
    getDataSyndicationSourceSubscriptionListAction: {
        status: "PENDING" | "SUCCESS" | "FAILED";
        error?: string;
    };
    newSourceSubscriptionAction: Record<string, Record<string, string>>;
    deleteDataSyndicationSubscriptionActions: Record<string, { status: string; error?: IActionError }>;
    getDataSyndicationSourceSubscriptionList: (sourceId: string) => void;
    createDataSyndicationSourceSubscription: (sourceId: string, subscription: IDataSyndicationSourceSubscription) => void;
    deleteDataSyndicationSubscription: (subscriptionId: string) => void;
}

const DataSyndicationSourceSubscription: React.FC<DataSyndicationSourceSubscriptionProps> = ({
    sourceId,
    readOnly = false,
    subscriptionListResult,
    getDataSyndicationSourceSubscriptionListAction,
    newSourceSubscriptionAction,
    deleteDataSyndicationSubscriptionActions,
    getDataSyndicationSourceSubscriptionList,
    createDataSyndicationSourceSubscription,
    deleteDataSyndicationSubscription,
}) => {
    const [result, setResult] = useState<{ success: boolean; title: string; message: string; onOkClicked: () => void } | null>(null);
    const previousDeleteDataSyndicationSubscriptionActions = usePrevious(deleteDataSyndicationSubscriptionActions);

    useEffect(() => {
        if (sourceId) {
            getDataSyndicationSourceSubscriptionList(sourceId);
        }
    }, [sourceId, getDataSyndicationSourceSubscriptionList]);

    useEffect(() => {
        if (
            deleteDataSyndicationSubscriptionActions &&
            previousDeleteDataSyndicationSubscriptionActions &&
            deleteDataSyndicationSubscriptionActions !== previousDeleteDataSyndicationSubscriptionActions
        ) {
            const deletedSubscriptionId = Object.keys(deleteDataSyndicationSubscriptionActions).find((id) => {
                const currentAction = deleteDataSyndicationSubscriptionActions[id];
                const previousAction = previousDeleteDataSyndicationSubscriptionActions[id];
                return currentAction && currentAction !== previousAction;
            });

            if (deletedSubscriptionId) {
                const deleteAction = deleteDataSyndicationSubscriptionActions[deletedSubscriptionId];
                if (deleteAction.status === ActionStatus.SUCCESS) {
                    setResult({
                        success: true,
                        title: "Delete Subscription",
                        message: "Subscription deleted successfully.",
                        onOkClicked: () => setResult(null),
                    });
                } else if (deleteAction.status === ActionStatus.FAILED) {
                    setResult({
                        success: false,
                        title: "Delete Subscription",
                        message: deleteAction.error?.message || "Failed to delete subscription.",
                        onOkClicked: () => setResult(null),
                    });
                }
            }
        }
    }, [deleteDataSyndicationSubscriptionActions, previousDeleteDataSyndicationSubscriptionActions]);

    const handleSubscriptionSave = useCallback(
        (subscription: IDataSyndicationSourceSubscription) => {
            if (sourceId) {
                createDataSyndicationSourceSubscription(sourceId, subscription);
            }
        },
        [sourceId, createDataSyndicationSourceSubscription],
    );

    const handleDeleteSubscription = useCallback(
        (subscription: IDataSyndicationSourceSubscription) => {
            if (subscription.subscriptionId) {
                Modal.confirm({
                    title: "Delete Subscription",
                    content: "Are you sure you want to delete this subscription?",
                    okText: "Yes",
                    cancelText: "No",
                    centered: true,
                    onOk: () => {
                        deleteDataSyndicationSubscription(subscription.subscriptionId!);
                    },
                });
            }
        },
        [deleteDataSyndicationSubscription],
    );

    const isLoading = getDataSyndicationSourceSubscriptionListAction.status === "PENDING";
    const isError = getDataSyndicationSourceSubscriptionListAction.status === "FAILED";

    if (isError) {
        return (
            <Result
                status="error"
                title="Failed to load subscriptions"
                subTitle={getDataSyndicationSourceSubscriptionListAction.error || "An error occurred while loading the subscriptions."}
                extra={[
                    <button key="retry" onClick={() => sourceId && getDataSyndicationSourceSubscriptionList(sourceId)} className="ant-btn ant-btn-primary">
                        Try Again
                    </button>,
                ]}
            />
        );
    }

    return (
        <>
            {result && (
                <>
                    <SuccessResult show={result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked} okText="OK" />
                    <ErrorResult show={!result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked} okText="OK" />
                </>
            )}
            <LoadingOverlay busy={isLoading} message="Loading subscriptions..." />
            <DataSyndicationSourceSubscriptionList
                accounts={sourceId === subscriptionListResult.sourceId ? subscriptionListResult.Items : []}
                onSubscriptionSave={handleSubscriptionSave}
                onDeleteSubscription={handleDeleteSubscription}
                readOnly={readOnly}
                subscriptionAction={newSourceSubscriptionAction}
            />
        </>
    );
};

const mapStateToProps = (state: any) => ({
    subscriptionListResult: state.dataSyndicationSource.subscriptionListResult,
    getDataSyndicationSourceSubscriptionListAction: state.dataSyndicationSource.getDataSyndicationSourceSubscriptionListAction,
    newSourceSubscriptionAction: state.dataSyndicationSource.newSourceSubscriptionAction,
    deleteDataSyndicationSubscriptionActions: state.dataSyndicationSubscription.deleteDataSyndicationSubscriptionActions,
});

const mapDispatchToProps = (dispatch: any) => ({
    getDataSyndicationSourceSubscriptionList: (sourceId: string) => dispatch(getDataSyndicationSourceSubscriptionListRequest(sourceId)),
    createDataSyndicationSourceSubscription: (sourceId: string, subscription: IDataSyndicationSourceSubscription) =>
        dispatch(createDataSyndicationSourceSubscriptionRequest(sourceId, subscription)),
    deleteDataSyndicationSubscription: (subscriptionId: string) => dispatch(deleteDataSyndicationSubscriptionRequest(subscriptionId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataSyndicationSourceSubscription);
